import styled, { css } from 'styled-components';

import { LegacyButton } from '@/components/LegacyButton';
import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';
import { px2rem } from '@/theme';

import { ReactComponent as ShowLess } from '@/assets/icons/show-less.svg';
import { ReactComponent as ShowMore } from '@/assets/icons/show-more.svg';

export const ToggleButton = styled(LegacyButton)`
  text-transform: none;
  white-space: nowrap;
  ${({ theme }) => css`
    font-size: ${theme.fontSizes.p3};
    ${theme.media.md.up} {
      font-size: ${theme.fontSizes.p2};
    }
  `}
`;

type TextProps = {
  type?: string | null | undefined;
};

export const ListItem = styled.li`
  margin-left: 0 !important;
`;

export const StyledEm = styled.em`
  font-style: italic;
`;
export const StyledH1 = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes.h1};
  line-height: ${({ theme }) => theme.lineHeights.h1};
  margin: ${({ theme }) => theme.space.s300} 0;
  font-family: ${({ theme }) => theme.fontFamilies.italianPlate};
  text-transform: uppercase;
`;
export const StyledH2 = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.h2};
  line-height: ${({ theme }) => theme.lineHeights.h2};
  margin: ${({ theme }) => theme.space.s250} 0;
  font-family: ${({ theme }) => theme.fontFamilies.italianPlate};
  text-transform: uppercase;
`;
export const StyledH3 = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.h3};
  line-height: ${({ theme }) => theme.lineHeights.h3};
  margin: ${({ theme }) => theme.space.s200} 0;
  font-family: ${({ theme }) => theme.fontFamilies.italianPlate};
  text-transform: uppercase;
`;
export const StyledH4 = styled.h4`
  font-size: ${({ theme }) => theme.fontSizes.h4};
  line-height: ${({ theme }) => theme.lineHeights.h4};
  margin: ${({ theme }) => theme.space.s150} 0;
  font-family: ${({ theme }) => theme.fontFamilies.italianPlate};
  text-transform: uppercase;
`;
export const StyledH5 = styled.h5`
  font-size: ${({ theme }) => theme.fontSizes.h5};
  line-height: ${({ theme }) => theme.lineHeights.h5};
  margin: ${({ theme }) => theme.space.s100} 0;
  font-family: ${({ theme }) => theme.fontFamilies.italianPlate};
  text-transform: uppercase;
`;
export const StyledH6 = styled.h6`
  font-size: ${({ theme }) => theme.fontSizes.h6};
  line-height: ${({ theme }) => theme.lineHeights.h6};
  margin: ${({ theme }) => theme.space.s050} 0;
  font-family: ${({ theme }) => theme.fontFamilies.italianPlate};
  text-transform: uppercase;
`;
export const StyledQuote = styled.blockquote`
  font-style: italic;
  margin: 1em 0;
  padding: 0.5em 1em;
  border-left: 4px solid #ccc;
  background: ${({ theme }) => theme.colors.secondaryLightGrey};
  color: ${({ theme }) => theme.colors.quoteGrey};
`;
export const StyledCode = styled.code`
  font-family: 'Courier New', Courier, monospace;
  background: #f5f5f5;
  padding: 0.2em 0.4em;
  border-radius: 3px;
  color: ${({ theme }) => theme.colors.codeRed};
`;
export const StyledSuperscript = styled.sup`
  font-size: 0.75em;
  vertical-align: super;
  line-height: 0;
`;
export const StyledSubscript = styled.sub`
  font-size: 0.75em;
  vertical-align: sub;
  line-height: 0;
`;

export const StyledLink = styled(LinkWithLocale)<{ backgroundColor?: string }>`
  cursor: pointer;
  letter-spacing: 0;
  font-weight: 700;
  transition: all 0.2s ease-in-out;
  text-decoration: underline;

  ${({ backgroundColor, theme }) => css`
    color: ${theme.colors.primaryBlue};

    &:hover {
      color: ${backgroundColor &&
      backgroundColor.toUpperCase() !== theme.colors.primary &&
      backgroundColor.toUpperCase() !== theme.colors.white
        ? backgroundColor
        : theme.colors.primaryBlue};
    }
  `}
`;
export const Text = styled.div<TextProps & { $isCountDown?: boolean | null }>`
  word-break: break-word;
  ul {
    list-style-type: disc;
    margin-left: ${({ theme }) => theme.spacing(2)};
    margin-top: ${({ theme }) => theme.spacing(1)};
  }

  ol {
    list-style-type: decimal;
    margin-left: ${({ theme }) => theme.spacing(2)};
  }
  li {
    width: fit-content;
    margin-left: ${({ theme }) => theme.spacing(2)};
  }
  li > p {
    display: inline-block;
    width: auto;
  }

  ${({ color, type }) => css`
  min-height: ${type === 'Icon' ? '70px' : 'auto'};
    color:${color};
    }
  `}
  ${({ theme }) => css`
    font-size: ${theme.fontSizes.p2};
    line-height: ${theme.lineHeights.p2};
  `}
  ${({ theme, type }) =>
    type === 'IntroText' &&
    css`
      font-size: ${theme.fontSizes.p1};
      font-weight: ${theme.fontWeights.semibold};
      font-family: ${theme.fontFamilies.moderat};
      line-height: 150%;
    `}
  ${({ $isCountDown, theme }) =>
    $isCountDown &&
    css`
      margin-top: ${theme.spacing(2)};
      ${theme.media.md.up} {
        margin-top: ${theme.spacing(3)};
      }
    `}
`;

export const StyledOl = styled.ol`
  &&& {
    padding-bottom: ${({ theme }) => theme.spacing(1)};
    margin-left: ${({ theme }) => theme.spacing(1)};

    li {
      position: relative;
      margin-left: 0;
      display: flex;
      align-items: baseline;
      counter-increment: list-counter;
      text-align: left;

      &::before {
        content: counter(list-counter);
        ${({ theme }) => css`
          top: ${theme.spacing(1)};
          margin-inline: ${px2rem(5, 10)};
        `}
      }
    }
  }
`;

export const StyledTable = styled.table`
  ${({ theme }) => css`
    color: ${theme.colors.default};
  `}
  letter-spacing: normal;
  line-height: normal;
  padding: 0;
  height: auto;
  min-height: 0;
  max-height: none;
  min-width: 0;
  max-width: none;
  clear: none;
  float: none;
  position: static;
  bottom: auto;
  left: auto;
  right: auto;
  top: auto;
  text-align: left;
  text-decoration: none;
  text-indent: 0;
  text-shadow: none;
  text-transform: none;
  white-space: normal;
  background: none;
  vertical-align: baseline;
  z-index: auto;
  box-shadow: none;
  border-collapse: inherit;
  margin: auto;
  margin-top: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.colors.borderGrey};
  border-radius: 5px;
  border-spacing: initial;
  width: 100%;
  > tbody {
    width: 100%;
  }
  > tbody > tr:last-child > td {
    border-bottom: none;
  }

  ${({ theme }) => css`
    ${theme.media.sm.down} {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }
  `}
`;

export const StyledRow = styled.tr<{ data: string[] }>`
  font-size: 1rem;
  border-collapse: inherit;
  border-spacing: initial;
  box-sizing: content-box;
  font-family: inherit;
  font-weight: normal;
  letter-spacing: normal;
  line-height: normal;
  padding: 0;
  margin: 0;
  height: auto;
  min-height: 0;
  max-height: none;
  width: auto;
  min-width: 0;
  max-width: none;
  border-radius: 0;
  border: none;
  clear: none;
  float: none;
  position: static;
  bottom: auto;
  left: auto;
  right: auto;
  top: auto;
  text-align: left;
  text-decoration: none;
  text-indent: 0;
  text-shadow: none;
  text-transform: none;
  white-space: normal;
  background: none;
  overflow: visible;
  vertical-align: baseline;
  visibility: visible;
  z-index: auto;
  box-shadow: none;
  > th {
    border-collapse: inherit;
    border-spacing: initial;
    font-family: inherit;
    letter-spacing: normal;
    line-height: normal;
    text-indent: 0;
    text-shadow: none;
    text-transform: none;
    white-space: normal;
    padding: 12px 15px;
    text-align: left;
    min-width: 75px;
    color: #0e2240;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderGrey};
    border-right: 1px solid ${({ theme }) => theme.colors.borderGrey};
    ${({ theme }) => css`
      background-color: ${theme.colors.mediumGrey};
    `}
    font-size: 0.9em;
    width: 25%;
  }
  ${({ theme }) => css`
    ${theme.media.sm.down} {
      > th {
        display: table-cell;
        border-right: 1px solid ${({ theme }) => theme.colors.borderGrey};
      }
    }
  `}
  > th > p {
    margin: 0;
  }
  > th:last-child {
    border-right: none;
  }

  > td:last-child {
    border-right: none;
  }
`;

export const StyledCell = styled.td`
  vertical-align: middle;
  border-collapse: inherit;
  border-spacing: initial;
  font-family: inherit;
  font-weight: normal;
  letter-spacing: normal;
  line-height: normal;
  margin: 0;
  height: auto;
  min-height: 0;
  max-height: none;
  width: auto;
  min-width: 0;
  max-width: none;
  border-radius: 0;
  border: none;
  clear: none;
  float: none;
  position: static;
  bottom: auto;
  left: auto;
  right: auto;
  top: auto;
  text-decoration: none;
  text-indent: 0;
  text-shadow: none;
  text-transform: none;
  white-space: normal;
  background: none;
  overflow: visible;
  vertical-align: baseline;
  visibility: visible;
  z-index: auto;
  box-shadow: none;
  padding: 12px 15px;
  text-align: left;
  font-size: 0.9em;
  color: #000;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderGrey};
  border-right: 1px solid ${({ theme }) => theme.colors.borderGrey};
  ${({ theme }) => css`
    ${theme.media.sm.down} {
      &&& {
        border-bottom: 1px solid ${({ theme }) => theme.colors.borderGrey};
        display: table-cell;
        border-right: 1px solid ${({ theme }) => theme.colors.borderGrey};
      }
    }
  `}
  > p {
    margin: 0;
  }
`;

export const ShowLessIcon = styled(ShowLess)`
  height: 15px;
  width: 29px;
  padding: 4px 2px 0 2px;
`;
export const ShowMoreIcon = styled(ShowMore)`
  height: 15px;
  width: 29px;
  padding: 4px 2px 0 2px;
`;
